import { useEffect, useReducer } from 'react';
import {
    AlloyPageErrors,
    AlloyPageState,
    AlloyProcessStages,
    AlloyWheelRepairRequest,
    ReducerActionNames
} from '../sitePages/alloyRepairRequest/AlloyRepairRequestTypes';
import AlloyRepairRequestReducer from '../sitePages/alloyRepairRequest/AlloyRepairRequestReducer';
import {
    ProductTypes,
    RoutePaths,
    UploadImageTypes
} from '../../Constants/Common';
import { useNavigate } from 'react-router-dom';
import { RepairRequestApi } from '../../api/RepairRequestApi';
import { useAuth0 } from '@auth0/auth0-react';
import { ClientDateHandler } from '../common/clientDateHandler/ClientDateHandler';
import { CommonValidation } from '../common/commonValidation/commonValidation';
import { SelectInputHelper } from '../common/selectInput/SelectInput';
import { PolicyHolderApi } from '../../api/PolicyHolderApi';
import {
    AddressType,
    PolicyHolderType,
    ProductType,
    VehicleType
} from '../common/CommonTypes';
import { ProgressBarItem } from '../common/progressBar/ProgressBar';
import { AlloyRepairRequestContextState } from '../../context/AlloyRepairRequestContext';
import { GoogleAnalytics } from '../../classes/GoogleAnalytics';
import { ConfirmationLocationState } from '../sitePages/ConfirmationPage';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'state/hooks';
import { selectIsUSSite } from 'state/selectors/site';

function useAlloyRepairRequest(
    initialState: AlloyPageState,
    product: ProductType,
    vehicle: VehicleType
): AlloyRepairRequestContextState {
    const { user, getAccessTokenSilently } = useAuth0();
    const navigate = useNavigate();

    const [alloyRepairRequest, dispatch] = useReducer(
        AlloyRepairRequestReducer,
        initialState
    );

    const { t } = useTranslation();
    const isUsSite = useAppSelector(selectIsUSSite);
    const distanceImageMsg = isUsSite
        ? t('image_from_3feet_away_is_required')
        : t('image_from_1m_away_is_required');

    useEffect(() => {
        const getPolicyHolderDetails = async () => {
            const token = await getAccessTokenSilently();

            new PolicyHolderApi().getPolicyHolderDetails(
                product.reference,
                token,
                onGetPolicyHolderSuccess,
                onGetPolicyHolderFailure
            );
        };
        if (!isUsSite) {
            getPolicyHolderDetails();
        }
    }, []);

    const onGetPolicyHolderSuccess = (
        policyHolderDetails: PolicyHolderType
    ) => {
        dispatch({
            type: ReducerActionNames.SetPolicyData,
            address: policyHolderDetails.address
        });
    };

    const onGetPolicyHolderFailure = (error: string) => {
        console.log(`Failed to get the policy holder details: ${error}`);
    };

    const {
        alloyStage,
        alloyType,
        mileage,
        permissionGranted,
        alloyWheelRepairRequests,
        currentRepairRequest,
        repairAddresses,
        repairAddressSelectedIndex,
        updateWheel,
        scrollToAddress
    } = alloyRepairRequest;

    const progressBarItems: ProgressBarItem[] = [
        {
            order: 1,
            text: t('alloy_repair_request.progress_bar.text.general'),
            active: alloyStage === AlloyProcessStages.General,
            disabled: false,
            visible: true
        },
        {
            order: 2,
            text: t('alloy_repair_request.progress_bar.text.wheel_selection'),
            active: alloyStage === AlloyProcessStages.WheelSelection,
            disabled: false,
            visible: true
        },
        {
            order: 3,
            text: t('alloy_repair_request.progress_bar.text.summary'),
            active: alloyStage === AlloyProcessStages.Summary,
            disabled: false,
            visible: true
        },
        {
            order: 4,
            text: t('alloy_repair_request.progress_bar.text.confirmation'),
            active: false,
            disabled: false,
            visible: true
        }
    ];

    const onSelectAlloyType = (selectionName: string) => {
        dispatch({
            type: ReducerActionNames.SelectAlloyType,
            alloyType: selectionName
        });
    };

    const onChangeMileage = (event: React.FormEvent<HTMLInputElement>) => {
        dispatch({
            type: ReducerActionNames.UpdateMileage,
            mileage: event.currentTarget.value
        });
    };

    const onWheelValueChange = (key: string, value: string) => {
        dispatch({
            type: ReducerActionNames.UpdateWheelRepairRequest,
            key,
            value
        });
    };

    const onAlloyWheelRepairRequestChangeWheel = (
        value: string,
        key: string
    ): void => {
        dispatch({
            type: ReducerActionNames.UpdateWheelRepairRequest,
            key,
            value
        });
    };

    const onWheelSelected = (wheel: string) => {
        dispatch({
            type: ReducerActionNames.SetWheel,
            wheel,
            productReference: product.reference
        });
    };

    const onChangeDamageType = (selectionName: string) => {
        dispatch({
            type: ReducerActionNames.UpdateWheelRepairRequest,
            key: 'damageType',
            value: selectionName
        });
    };

    const onRepairAddressSelectionChanged = (selectedIndex: number) => {
        dispatch({
            type: ReducerActionNames.UpdateRepairAddressSelected,
            value: selectedIndex
        });
    };

    const onUploadImageChange = (
        imageType: string,
        uploadedPath: string,
        imageId: string,
        image: string,
        filename: string
    ) => {
        const uploadedImages = currentRepairRequest.uploadedImages;

        const uploadedImage = uploadedImages.find((ui) => ui.type == imageType);

        if (uploadedImage) {
            uploadedImage.uploadedPath = uploadedPath;
            uploadedImage.image = CommonValidation.isNotEmpty(uploadedPath)
                ? image
                : undefined;
            uploadedImage.filename = CommonValidation.isNotEmpty(uploadedPath)
                ? filename
                : undefined;
            uploadedImage.imageId = imageId;

            dispatch({
                type: ReducerActionNames.UpdateUploadedImages,
                uploadedImages: uploadedImages
            });
        }
    };

    const onFinishAddingWheels = (): boolean => {
        const formWasValid = validateCurrentAlloyWheelRepairRequest();

        if (formWasValid) {
            dispatch({ type: ReducerActionNames.AddWheelRequest });
            onChangeStage(AlloyProcessStages.WheelSelection);
        }
        dispatch({
            type: ReducerActionNames.SetHasErrors,
            hasErrors: !formWasValid
        });

        return formWasValid;
    };

    const validateCurrentAlloyWheelRepairRequest = () => {
        let errors: AlloyPageErrors = {};
        let isValid = true;

        if (CommonValidation.isEmpty(currentRepairRequest.damagedWheel)) {
            isValid = false;
            errors = {
                ...errors,
                alloyWheelSpecifics: {
                    ...errors.alloyWheelSpecifics,
                    damagedWheel: '1 wheel must be selected'
                }
            };
        }

        if (
            CommonValidation.isDateNull(
                currentRepairRequest.dateOfIncident,
                true
            ) ||
            CommonValidation.isEmpty(currentRepairRequest.dateOfIncident)
        ) {
            isValid = false;
            errors = {
                ...errors,
                alloyWheelSpecifics: {
                    ...errors.alloyWheelSpecifics,
                    dateOfIncident: t('date_of_incident_is_required')
                }
            };
        } else if (
            !CommonValidation.isValidDate(
                currentRepairRequest.dateOfIncident,
                true
            )
        ) {
            isValid = false;
            errors = {
                ...errors,
                alloyWheelSpecifics: {
                    ...errors.alloyWheelSpecifics,
                    dateOfIncident: 'Date of incident is out of a valid range'
                }
            };
        }

        if (SelectInputHelper.isNullValue(currentRepairRequest.damageType)) {
            isValid = false;
            errors = {
                ...errors,
                alloyWheelSpecifics: {
                    ...errors.alloyWheelSpecifics,
                    damageType: t('type_of_damage_is_required')
                }
            };
        }

        if (SelectInputHelper.isNullValue(currentRepairRequest.damageSize)) {
            isValid = false;
            errors = {
                ...errors,
                alloyWheelSpecifics: {
                    ...errors.alloyWheelSpecifics,
                    damageSize: t(
                        'quarters_of_the_wheel_are_damaged_is_required'
                    )
                }
            };
        }

        if (
            CommonValidation.isEmpty(
                currentRepairRequest.uploadedImages.find(
                    (ui) => ui.type == UploadImageTypes.CloseUp
                )?.uploadedPath
            )
        ) {
            isValid = false;
            errors = {
                ...errors,
                alloyWheelSpecifics: {
                    ...errors.alloyWheelSpecifics,
                    closeUpImage: t('close_up_image_is_required')
                }
            };
        }

        if (
            CommonValidation.isEmpty(
                currentRepairRequest.uploadedImages.find(
                    (ui) => ui.type === UploadImageTypes.Distance
                )?.uploadedPath
            )
        ) {
            isValid = false;
            errors = {
                ...errors,
                alloyWheelSpecifics: {
                    ...errors.alloyWheelSpecifics,
                    distanceImage: distanceImageMsg
                }
            };
        }

        dispatch({
            type: ReducerActionNames.UpdateAlloyErrors,
            alloyErrors: errors
        });

        return isValid;
    };

    const validateGeneralRepairRequestInfo = (): boolean => {
        let errors: AlloyPageErrors = {};
        let isValid = true;

        if (SelectInputHelper.isNullValue(alloyType)) {
            isValid = false;
            errors = { alloyType: t('alloy_type_is_required'), ...errors };
            GoogleAnalytics.trackEventsGoogleAnalytics(
                'Button click',
                'Alloy wheel button clicks',
                'Clicked continue without selecting type of wheel'
            );
        }

        if (CommonValidation.isEmpty(mileage)) {
            isValid = false;
            errors = {
                mileage: t('approximate_mileage_is_required'),
                ...errors
            };
            GoogleAnalytics.trackEventsGoogleAnalytics(
                'Button click',
                'Alloy wheel button clicks',
                'Clicked continue without providing mileage'
            );
        } else {
            if (!CommonValidation.isInteger(mileage)) {
                isValid = false;
                errors = {
                    mileage: t('approx_mileage_is_number'),
                    ...errors
                };
            }
        }

        if (repairAddressSelectedIndex < 0) {
            isValid = false;
            errors = {
                ...errors,
                noRepairAddressSelected: t('select_repair_address')
            };
            GoogleAnalytics.trackEventsGoogleAnalytics(
                'Button click',
                'Alloy wheel button clicks',
                'Clicked continue without selecting address'
            );
        }

        dispatch({
            type: ReducerActionNames.UpdateAlloyErrors,
            alloyErrors: errors
        });

        return isValid;
    };

    const onTogglePermission = (isChecked: boolean) => {
        dispatch({
            type: ReducerActionNames.SetPermissionGranted,
            permissionGranted: isChecked
        });
    };

    const onContinueToRepairAddress = (): void => {
        const validGeneralDetails = validateGeneralRepairRequestInfo();
        if (validGeneralDetails) {
            onChangeStage(AlloyProcessStages.Summary);
        }
    };

    const onDeleteRepairRequest = (wheel: string) => {
        dispatch({
            type: ReducerActionNames.RemoveWheel,
            wheel: wheel,
            newStage: AlloyProcessStages.WheelSelection
        });
    };

    const onRepairAddressUpdate = (defaultDealerAddress: AddressType) => {
        dispatch({
            type: ReducerActionNames.SetPolicyData,
            address: defaultDealerAddress
        });
    };

    const onContinueRepairRequest = (): void => {
        const validGeneralDetails = validateGeneralRepairRequestInfo();
        if (validGeneralDetails) {
            onChangeStage(AlloyProcessStages.WheelSelection);
        }

        dispatch({
            type: ReducerActionNames.SetHasErrors,
            hasErrors: !validGeneralDetails
        });
    };

    const onChangeStage = (stage: AlloyProcessStages): void => {
        dispatch({
            type: ReducerActionNames.UpdateAlloyStage,
            newStage: stage
        });

        if (CommonValidation.isNotEmpty(updateWheel)) {
            onUpdateWheelClick('');
        }

        if (scrollToAddress) {
            onUpdateAddressClick(false);
        }
    };

    const onFinishedAlloyRepairRequest = () => {
        const validPage = validateRepairPage();

        if (validPage) {
            saveRepairRequest();
        }
    };

    const validateRepairPage = () => {
        let errors: AlloyPageErrors = {};
        let isValid = true;

        if (!permissionGranted) {
            isValid = false;
            errors = {
                ...errors,
                permissionGranted: t('validate_agreement')
            };
        }

        dispatch({
            type: ReducerActionNames.UpdateAlloyErrors,
            alloyErrors: errors
        });

        return isValid;
    };

    const saveRepairRequest = async () => {
        const successCallback = (repairRequests) => {
            const activeConfirmationItems = progressBarItems.map((p) =>
                p.text === 'Confirmation'
                    ? { ...p, active: true }
                    : { ...p, active: false }
            );

            const state: ConfirmationLocationState = {
                progressBarItems: activeConfirmationItems,
                repairRequests,
                productType: ProductTypes.Alloy
            };

            navigate(RoutePaths.Confirmation, { state });
        };

        const failureCallback = (error) => {
            console.log(`Failed to register policy: ${error}`);

            navigate(RoutePaths.ConfirmationError);
        };

        dispatch({
            type: ReducerActionNames.LoadingOnRequestSubmission,
            loadingOnRequestSubmission: true
        });

        const repairRequests = alloyWheelRepairRequests.map((request) =>
            getRepairRequest(request)
        );

        console.log(`Repair requests: ${JSON.stringify(repairRequests)}`);

        const token = await getAccessTokenSilently();

        new RepairRequestApi().save(
            repairRequests,
            token,
            successCallback,
            failureCallback
        );
    };

    const getRepairRequest = (request: AlloyWheelRepairRequest) => {
        const repairRequest = {
            submitterEmailAddress: user.email,
            productReference: request.productReference,
            alloyType: alloyType,
            mileage: mileage,
            damagedWheel: request.damagedWheel,
            dateOfIncident: mapJsonDateToServer(request.dateOfIncident),
            damageType: request.damageType,
            damageSize: request.damageSize,
            additionalInformation: request.additionalInformation,
            closeUpImageGuid: request.uploadedImages.find(
                (ui) => ui.type === UploadImageTypes.CloseUp
            ).imageId,
            distanceImageGuid: request.uploadedImages.find(
                (ui) => ui.type === UploadImageTypes.Distance
            ).imageId,
            additionalImage1Guid:
                request.uploadedImages.find(
                    (ui) => ui.type === UploadImageTypes.AdditionalImage1
                ).imageId || '00000000-0000-0000-0000-000000000000',
            additionalImage2Guid:
                request.uploadedImages.find(
                    (ui) => ui.type === UploadImageTypes.AdditionalImage2
                ).imageId || '00000000-0000-0000-0000-000000000000',
            repairAddress: repairAddresses[repairAddressSelectedIndex]
        };
        return repairRequest;
    };

    const mapJsonDateToServer = (dateJson: string) => {
        const cdh = new ClientDateHandler();
        cdh.parseJSON(dateJson);

        const localDate = cdh.getLocalDate();
        return localDate;
    };

    const onUpdateWheelClick = (wheel: string): void => {
        dispatch({
            type: ReducerActionNames.UpdateWheel,
            updateWheel: wheel
        });
    };

    const onUpdateAddressClick = (scrollToAddress): void => {
        console.log('scrollToAddress', scrollToAddress);
        dispatch({
            type: ReducerActionNames.ScrollToAddress,
            scrollToAddress: scrollToAddress
        });
    };

    const onCancelClick = () => {
        dispatch({
            type: ReducerActionNames.ShowCancelAlert,
            showCancelAlert: true
        });
    };

    const onCancelRepairRequest = () => {
        GoogleAnalytics.trackEventsGoogleAnalytics(
            'Button click',
            'Alloy wheel button clicks',
            'Clicked cancel repair request'
        );
        navigate(RoutePaths.Home);
    };

    const onCancelUndo = () => {
        dispatch({
            type: ReducerActionNames.ShowCancelAlert,
            showCancelAlert: false
        });
    };

    return {
        product,
        vehicle,
        progressBarItems,
        alloyRepairRequest,
        onSelectAlloyType,
        onChangeMileage,
        onWheelValueChange,
        onAlloyWheelRepairRequestChangeWheel,
        onWheelSelected,
        onChangeDamageType,
        onRepairAddressSelectionChanged,
        onUploadImageChange,
        onFinishAddingWheels,
        onTogglePermission,
        onFinishedAlloyRepairRequest,
        onContinueToRepairAddress,
        onContinueRepairRequest,
        onDeleteRepairRequest,
        onCancelClick,
        onCancelRepairRequest,
        onCancelUndo,
        onChangeStage,
        onUpdateWheelClick,
        onUpdateAddressClick,
        onRepairAddressUpdate
    };
}

export default useAlloyRepairRequest;
